import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["image", "canvas", "placeholder", "zoneList"]
  static values = {
    zones: Array,
    imageUrl: String
  }

  connect() {
    this.markers = new Map()
    this.draggingMarker = null
    this.dragOffset = { x: 0, y: 0 }
    this.imgBounds = null

    if (this.imageUrlValue) {
      this.loadImage(this.imageUrlValue)
    }

    this.initializeMarkers()
    this.bindEvents()

    // Reposition on resize
    this.boundResize = this.handleResize.bind(this)
    window.addEventListener('resize', this.boundResize)
  }

  disconnect() {
    this.unbindEvents()
    window.removeEventListener('resize', this.boundResize)
  }

  bindEvents() {
    this.boundMouseMove = this.handleMouseMove.bind(this)
    this.boundMouseUp = this.handleMouseUp.bind(this)
    document.addEventListener('mousemove', this.boundMouseMove)
    document.addEventListener('mouseup', this.boundMouseUp)
  }

  unbindEvents() {
    document.removeEventListener('mousemove', this.boundMouseMove)
    document.removeEventListener('mouseup', this.boundMouseUp)
  }

  handleResize() {
    clearTimeout(this.resizeTimeout)
    this.resizeTimeout = setTimeout(() => {
      this.calculateImageBounds()
      this.repositionMarkers()
    }, 100)
  }

  // Handle file selection
  fileSelected(event) {
    const file = event.target.files[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = (e) => {
      this.loadImage(e.target.result)
    }
    reader.readAsDataURL(file)
  }

  loadImage(url) {
    if (this.hasPlaceholderTarget) {
      this.placeholderTarget.classList.add('hidden')
    }
    if (this.hasCanvasTarget) {
      this.canvasTarget.classList.remove('hidden')
      this.canvasTarget.classList.add('has-image')
    }
    if (this.hasImageTarget) {
      this.imageTarget.src = url
      this.imageTarget.classList.remove('hidden')
      this.imageTarget.onload = () => {
        this.calculateImageBounds()
        this.repositionMarkers()
      }
    }
  }

  calculateImageBounds() {
    if (!this.hasImageTarget || !this.hasCanvasTarget) return

    const img = this.imageTarget
    const canvas = this.canvasTarget
    const canvasRect = canvas.getBoundingClientRect()

    if (!img.naturalWidth || !img.naturalHeight) return

    const imgNaturalRatio = img.naturalWidth / img.naturalHeight
    const canvasRatio = canvasRect.width / canvasRect.height

    let imgWidth, imgHeight, imgLeft, imgTop

    if (imgNaturalRatio > canvasRatio) {
      imgWidth = canvasRect.width
      imgHeight = canvasRect.width / imgNaturalRatio
      imgLeft = 0
      imgTop = (canvasRect.height - imgHeight) / 2
    } else {
      imgHeight = canvasRect.height
      imgWidth = canvasRect.height * imgNaturalRatio
      imgLeft = (canvasRect.width - imgWidth) / 2
      imgTop = 0
    }

    this.imgBounds = { width: imgWidth, height: imgHeight, left: imgLeft, top: imgTop }
  }

  initializeMarkers() {
    if (!this.hasCanvasTarget) return

    this.zonesValue.forEach(zone => {
      this.createMarker(zone)
    })
  }

  createMarker(zone) {
    const marker = document.createElement('div')
    marker.className = 'map-marker'
    marker.dataset.zoneId = zone.id
    marker.dataset.mapX = zone.map_x
    marker.dataset.mapY = zone.map_y
    marker.style.backgroundColor = zone.color || '#00d4aa'
    marker.innerHTML = `
      <span class="map-marker-label">${zone.code || zone.name.substring(0, 3)}</span>
    `

    if (zone.map_x !== null && zone.map_y !== null) {
      marker.classList.add('placed')
    } else {
      marker.classList.add('unplaced')
    }

    marker.addEventListener('mousedown', (e) => this.startDrag(e, marker, zone))

    this.canvasTarget.appendChild(marker)
    this.markers.set(zone.id, { element: marker, zone })

    this.updateZoneListItem(zone.id, zone.map_x, zone.map_y)
  }

  startDrag(event, marker, zone) {
    event.preventDefault()
    this.calculateImageBounds()
    this.draggingMarker = { marker, zone }

    const rect = marker.getBoundingClientRect()
    this.dragOffset = {
      x: event.clientX - rect.left - rect.width / 2,
      y: event.clientY - rect.top - rect.height / 2
    }

    marker.classList.add('dragging')
  }

  handleMouseMove(event) {
    if (!this.draggingMarker || !this.imgBounds) return

    const canvasRect = this.canvasTarget.getBoundingClientRect()
    const { left: imgLeft, top: imgTop, width: imgWidth, height: imgHeight } = this.imgBounds

    let x = event.clientX - canvasRect.left - this.dragOffset.x
    let y = event.clientY - canvasRect.top - this.dragOffset.y

    // Constrain to image bounds (not canvas)
    x = Math.max(imgLeft, Math.min(x, imgLeft + imgWidth))
    y = Math.max(imgTop, Math.min(y, imgTop + imgHeight))

    this.draggingMarker.marker.style.left = `${x}px`
    this.draggingMarker.marker.style.top = `${y}px`
  }

  handleMouseUp(event) {
    if (!this.draggingMarker || !this.imgBounds) return

    const { marker, zone } = this.draggingMarker
    marker.classList.remove('dragging')
    marker.classList.add('placed')
    marker.classList.remove('unplaced')

    const canvasRect = this.canvasTarget.getBoundingClientRect()
    const { left: imgLeft, top: imgTop, width: imgWidth, height: imgHeight } = this.imgBounds

    let x = event.clientX - canvasRect.left
    let y = event.clientY - canvasRect.top

    // Constrain to image bounds
    x = Math.max(imgLeft, Math.min(x, imgLeft + imgWidth))
    y = Math.max(imgTop, Math.min(y, imgTop + imgHeight))

    // Convert to percentage relative to image
    const percentX = ((x - imgLeft) / imgWidth) * 100
    const percentY = ((y - imgTop) / imgHeight) * 100

    // Store for repositioning
    marker.dataset.mapX = percentX
    marker.dataset.mapY = percentY

    // Update hidden inputs
    this.updateZonePosition(zone.id, percentX, percentY)

    this.draggingMarker = null
  }

  updateZonePosition(zoneId, x, y) {
    // Update hidden inputs
    const xInput = document.querySelector(`input[name="zones[${zoneId}][map_x]"]`)
    const yInput = document.querySelector(`input[name="zones[${zoneId}][map_y]"]`)

    if (xInput) xInput.value = x.toFixed(2)
    if (yInput) yInput.value = y.toFixed(2)

    this.updateZoneListItem(zoneId, x, y)
  }

  updateZoneListItem(zoneId, x, y) {
    const listItem = document.querySelector(`[data-zone-list-id="${zoneId}"]`)
    if (!listItem) return

    const statusBadge = listItem.querySelector('.zone-status')
    if (statusBadge) {
      if (x !== null && y !== null) {
        statusBadge.className = 'zone-status badge badge-success'
        statusBadge.textContent = 'Ubicada'
      } else {
        statusBadge.className = 'zone-status badge badge-neutral'
        statusBadge.textContent = 'Sin ubicar'
      }
    }
  }

  repositionMarkers() {
    if (!this.imgBounds) {
      this.calculateImageBounds()
    }
    if (!this.imgBounds) return

    const { left: imgLeft, top: imgTop, width: imgWidth, height: imgHeight } = this.imgBounds

    this.markers.forEach(({ element }) => {
      const mapX = parseFloat(element.dataset.mapX)
      const mapY = parseFloat(element.dataset.mapY)

      if (!isNaN(mapX) && !isNaN(mapY)) {
        const pixelX = imgLeft + (mapX / 100) * imgWidth
        const pixelY = imgTop + (mapY / 100) * imgHeight
        element.style.left = `${pixelX}px`
        element.style.top = `${pixelY}px`
      }
    })
  }

  // Remove marker position
  removeMarker(event) {
    const zoneId = event.currentTarget.dataset.zoneId
    const markerData = this.markers.get(parseInt(zoneId))

    if (markerData) {
      markerData.element.classList.remove('placed')
      markerData.element.classList.add('unplaced')
      markerData.element.style.left = ''
      markerData.element.style.top = ''
    }

    this.updateZonePosition(zoneId, '', '')
    this.updateZoneListItem(zoneId, null, null)
  }
}
